import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertTriangle, ArrowRight, Building2, Globe2, Mail, Phone } from "lucide-react";
import { useStore } from "../../lib/store";
import { showFeedback } from "../../lib/feedback";
import type {
  Company,
  Contact,
  IdentityMatchCandidate,
  PartyChannel,
  PartyChannelType,
} from "../../lib/types";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { Field, TextArea } from "../../ui/Field";
import { Select } from "../../ui/Select";
import { ToolbarSpacer } from "../../ui/PageHead";
import { useIdentityGate } from "./useIdentityGate";
import type {
  IdentityCandidateReviewDecision,
  MergeCompaniesPreview,
  MergeCompanyField,
  MergeCompanyFieldChoice,
  ReclassifyPartyChannelPreview,
} from "./commands";

/**
 * Diálogos de decisão humana da identidade empresarial, na linguagem da V2.
 *
 * Os três compartilham o mesmo contrato (AGENTS §27.2): prévia autoritativa
 * antes de confirmar, motivo obrigatório, confirmação explícita, chave de
 * idempotência por tentativa e nenhuma ação automática. A resposta de uma
 * prévia antiga nunca sobrescreve uma mais nova (§26.2), por isso cada efeito
 * carrega um número de sequência.
 */

function companyName(company: Company) {
  return company.tradeName || company.legalName;
}

function channelIcon(type: PartyChannelType) {
  if (type === "email") return <Mail size={15} aria-hidden="true" />;
  if (type === "phone") return <Phone size={15} aria-hidden="true" />;
  return <Globe2 size={15} aria-hidden="true" />;
}

function channelTypeLabel(type: PartyChannelType) {
  if (type === "email") return "E-mail";
  if (type === "phone") return "Telefone";
  return "Instagram";
}

function countLabel(value: number, singular: string) {
  return `${value} ${singular}${value === 1 ? "" : "s"}`;
}

function formatDateTime(value?: string) {
  if (!value) return "Data não informada";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Data não informada" : date.toLocaleString("pt-BR");
}

/** Caixa de confirmação nominal usada pelos três comandos. */
function ConfirmCheck({
  checked,
  onChange,
  disabled,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="cx-check">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{children}</span>
    </label>
  );
}

function Notice({ tone, children }: { tone: "info" | "alert"; children: ReactNode }) {
  return (
    <p className="cx-notice" data-tone={tone} role={tone === "alert" ? "alert" : "status"}>
      <AlertTriangle size={15} aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

/* --- Revisão de candidato --------------------------------------------------- */

function candidateSideTitle(company?: Company, contact?: Contact) {
  if (company) return companyName(company);
  if (contact) return contact.name;
  return "Registro referenciado";
}

function candidateSideMeta(company?: Company, contact?: Contact) {
  if (company)
    return (
      [company.segment, [company.city, company.state].filter(Boolean).join(" / "), company.cnpj]
        .filter(Boolean)
        .join(" · ") || "Sem dados adicionais informados"
    );
  if (contact) return contact.role || "Função não informada";
  return "Verifique o snapshot de identidade deste ambiente.";
}

function CandidateSide({
  label,
  company,
  contact,
}: {
  label: string;
  company?: Company;
  contact?: Contact;
}) {
  return (
    <div className="cx-compare-side">
      <small>{label}</small>
      <strong>{candidateSideTitle(company, contact)}</strong>
      <span>{company ? "Company canônica" : contact ? "Contact canônico" : "Referência não carregada"}</span>
      <p>{candidateSideMeta(company, contact)}</p>
    </div>
  );
}

export function ReviewCandidateDialog({
  candidate,
  onClose,
}: {
  candidate: IdentityMatchCandidate;
  onClose: () => void;
}) {
  const store = useStore();
  const gate = useIdentityGate();
  const [decision, setDecision] = useState<IdentityCandidateReviewDecision>("accepted");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [idempotencyKey] = useState(
    () => `identity.candidate.review:${candidate.id}:${crypto.randomUUID()}`,
  );

  const company = store.companies.find((item) => item.id === candidate.companyId);
  const contact = store.contacts.find((item) => item.id === candidate.contactId);
  const candidateCompany = store.companies.find((item) => item.id === candidate.candidateCompanyId);
  const candidateContact = store.contacts.find((item) => item.id === candidate.candidateContactId);
  const dirty = Boolean(reason.trim()) || confirmed || decision !== "accepted";

  const submit = async () => {
    if (saving) return;
    if (!reason.trim()) {
      setError("Informe o motivo da decisão humana.");
      return;
    }
    if (!confirmed) {
      setError("Confirme que a decisão não cria vínculo nem mesclagem automática.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await store.reviewIdentityCandidate({
        candidateId: candidate.id,
        decision,
        reason: reason.trim(),
        idempotencyKey,
        confirm: true,
      });
      if (!result) return;
      showFeedback(
        result.idempotent
          ? "Esta decisão já estava registrada."
          : decision === "accepted"
            ? "Sugestão aceita e auditada; nenhum vínculo foi criado."
            : "Sugestão rejeitada e auditada.",
        "success",
      );
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Não foi possível registrar a decisão.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={dirty}
      width="wide"
      title="Revisar possível correspondência"
      description="A decisão só muda o estado da sugestão e cria auditoria. Ela não vincula Companies, Contacts ou Leads e não executa mesclagem."
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button variant="primary" onClick={submit} loading={saving}>
            {decision === "accepted" ? "Registrar aceitação" : "Registrar rejeição"}
          </Button>
        </>
      }
    >
      <div className="cx-compare">
        <CandidateSide label="Contexto atual" company={company} contact={contact} />
        <span className="cx-compare-arrow" aria-hidden="true">
          <ArrowRight size={16} />
        </span>
        <CandidateSide label="Sugestão candidata" company={candidateCompany} contact={candidateContact} />
      </div>

      <div className="cx-evidence">
        <small>Evidência informada</small>
        <p>{candidate.reason}</p>
        <span>Criada em {formatDateTime(candidate.createdAt)}</span>
      </div>

      <Field label="Decisão" required>
        {({ id, describedBy }) => (
          <Select
            id={id}
            describedBy={describedBy}
            size="field"
            label="Decisão"
            value={decision}
            options={[
              { value: "accepted", label: "Aceitar como correspondência", hint: "Registra que a evidência basta, sem aplicar vínculo." },
              { value: "rejected", label: "Rejeitar sugestão", hint: "Mantém as identidades separadas." },
            ]}
            onChange={(value) => {
              setDecision(value as IdentityCandidateReviewDecision);
              setConfirmed(false);
              setError("");
            }}
            disabled={saving}
          />
        )}
      </Field>

      <Field label="Motivo da decisão" required>
        {({ id, describedBy, invalid }) => (
          <TextArea
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            rows={3}
            value={reason}
            disabled={saving}
            placeholder="Ex.: domínio igual, mas responsáveis e endereço são diferentes"
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>

      <ConfirmCheck checked={confirmed} onChange={setConfirmed} disabled={saving}>
        Confirmo a decisão humana e entendo que nenhum vínculo ou mesclagem automática será aplicado.
      </ConfirmCheck>

      {!gate.commandsReady && <Notice tone={gate.degraded ? "alert" : "info"}>{gate.reason}</Notice>}
      {error && (
        <p className="cx-form-alert" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

/* --- Reclassificação de canal ----------------------------------------------- */

function impactLabel(value: string) {
  if (value === "endpoint_owner") return "Muda somente o dono canônico deste endpoint.";
  if (value === "future_identity_resolution")
    return "A resolução futura de identidade passa a considerar o novo dono.";
  return value;
}

function conflictLabel(value: string) {
  if (value === "active_channel_with_same_company_type_and_value")
    return "Já existe um endpoint ativo com o mesmo tipo e valor nesta empresa.";
  return value;
}

export function ReclassifyChannelDialog({
  channel,
  company,
  contacts,
  onClose,
}: {
  channel: PartyChannel;
  company: Company;
  contacts: Contact[];
  onClose: () => void;
}) {
  const store = useStore();
  const gate = useIdentityGate();
  const previewChannel = store.previewReclassifyPartyChannel;
  const sequenceRef = useRef(0);
  const initialTarget =
    channel.contactId && contacts.some((item) => item.id === channel.contactId && !item.mergedIntoId)
      ? channel.contactId
      : "";
  const [targetContactId, setTargetContactId] = useState(initialTarget);
  const [preview, setPreview] = useState<ReclassifyPartyChannelPreview>();
  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [idempotencyKey] = useState(
    () => `identity.channel.reclassify:${channel.id}:${crypto.randomUUID()}`,
  );

  const options = useMemo(
    () => [
      { value: "", label: "Empresa — canal geral" },
      ...contacts
        .filter((contact) => !contact.mergedIntoId)
        .map((contact) => ({
          value: contact.id,
          label: contact.name,
          hint: contact.role,
        })),
    ],
    [contacts],
  );
  const currentOwner = channel.contactId
    ? contacts.find((contact) => contact.id === channel.contactId)
    : undefined;
  const targetOwner = contacts.find((contact) => contact.id === targetContactId);
  const dirty = targetContactId !== initialTarget || Boolean(reason.trim()) || confirmed;

  useEffect(() => {
    const sequence = ++sequenceRef.current;
    let active = true;
    setLoading(true);
    setPreview(undefined);
    setPreviewError("");
    void previewChannel(channel.id, targetContactId || null)
      .then((next) => {
        if (active && sequence === sequenceRef.current && next) setPreview(next);
      })
      .catch((cause) => {
        if (active && sequence === sequenceRef.current)
          setPreviewError(
            cause instanceof Error ? cause.message : "Não foi possível carregar a prévia do canal.",
          );
      })
      .finally(() => {
        if (active && sequence === sequenceRef.current) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [channel.id, previewChannel, targetContactId]);

  const submit = async () => {
    if (saving) return;
    if (!gate.commandsReady) {
      setError(gate.reason ?? "Os comandos de identidade não estão disponíveis neste ambiente.");
      return;
    }
    if (!preview || loading) {
      setError("Aguarde a prévia autoritativa antes de confirmar.");
      return;
    }
    if (preview.action === "block") {
      setError("A reclassificação está bloqueada por um conflito de canal ativo.");
      return;
    }
    if (!preview.requiresConfirmation) {
      setError("Escolha um dono diferente para reclassificar o canal.");
      return;
    }
    if (!reason.trim()) {
      setError("Informe o motivo da reclassificação.");
      return;
    }
    if (!confirmed) {
      setError("Confirme a prévia antes de reclassificar o canal.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await store.reclassifyPartyChannel({
        channelId: channel.id,
        targetContactId: targetContactId || null,
        reason: reason.trim(),
        idempotencyKey,
        confirm: true,
      });
      if (!result) return;
      showFeedback(
        result.idempotent ? "Esta reclassificação já estava registrada." : "Canal reclassificado e auditado.",
        "success",
      );
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Não foi possível reclassificar o canal.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={dirty}
      title="Escolher o dono do endpoint"
      description="Só a classificação do endpoint muda. Empresa, contatos, leads, jornadas e analytics permanecem intactos."
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button
            variant="primary"
            onClick={submit}
            loading={saving}
            disabled={
              loading ||
              !gate.commandsReady ||
              !preview ||
              preview.action === "block" ||
              !preview.requiresConfirmation
            }
          >
            Reclassificar canal
          </Button>
        </>
      }
    >
      <div className="cx-channel-head">
        {channelIcon(channel.type)}
        <span>
          <b>{channel.value}</b>
          <small>
            {channelTypeLabel(channel.type)} · valor normalizado: {channel.normalizedValue}
          </small>
        </span>
      </div>

      <div className="cx-compare">
        <div className="cx-compare-side">
          <small>Dono atual</small>
          <strong>{currentOwner ? currentOwner.name : `${companyName(company)} · empresa`}</strong>
          <p>{currentOwner?.role || "Canal geral da empresa"}</p>
        </div>
        <span className="cx-compare-arrow" aria-hidden="true">
          <ArrowRight size={16} />
        </span>
        <div className="cx-compare-side">
          <small>Novo dono</small>
          <strong>{targetOwner ? targetOwner.name : `${companyName(company)} · empresa`}</strong>
          <p>{targetOwner?.role || "Canal geral da empresa"}</p>
        </div>
      </div>

      <Field label="Novo dono" required>
        {({ id, describedBy }) => (
          <Select
            id={id}
            describedBy={describedBy}
            size="field"
            label="Novo dono"
            value={targetContactId}
            options={options}
            onChange={(value) => {
              setTargetContactId(value);
              setConfirmed(false);
              setError("");
            }}
            disabled={saving || !gate.commandsReady}
          />
        )}
      </Field>

      {loading && <p className="cx-note" role="status">Atualizando prévia autoritativa…</p>}
      {previewError && (
        <p className="cx-form-alert" role="alert">
          {previewError}
        </p>
      )}
      {preview && (
        <div className="cx-preview" data-blocked={preview.action === "block" || undefined}>
          <div className="cx-preview-head">
            <span>
              {preview.action === "block"
                ? "Reclassificação bloqueada"
                : preview.requiresConfirmation
                  ? "Prévia pronta para confirmação"
                  : "Nenhuma alteração de dono"}
            </span>
            <small>{preview.conflicts.length ? `${preview.conflicts.length} conflito(s)` : "Sem conflito"}</small>
          </div>
          {preview.conflicts.length > 0 && (
            <ul>
              {preview.conflicts.map((conflict) => (
                <li key={conflict}>{conflictLabel(conflict)}</li>
              ))}
            </ul>
          )}
          {preview.action === "reclassify" && preview.requiresConfirmation && (
            <ul>
              {preview.impact.map((item) => (
                <li key={item}>{impactLabel(item)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <Field label="Motivo da decisão" required>
        {({ id, describedBy, invalid }) => (
          <TextArea
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            rows={3}
            value={reason}
            disabled={saving || !gate.commandsReady}
            placeholder="Ex.: este telefone é usado exclusivamente pela responsável financeira"
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>

      <ConfirmCheck
        checked={confirmed}
        onChange={setConfirmed}
        disabled={saving || !gate.commandsReady}
      >
        Confirmo a prévia e entendo que apenas o dono do endpoint será alterado.
      </ConfirmCheck>

      {!gate.commandsReady && <Notice tone={gate.degraded ? "alert" : "info"}>{gate.reason}</Notice>}
      {error && (
        <p className="cx-form-alert" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

/* --- Mesclagem de empresas --------------------------------------------------- */

const MERGE_FIELD_LABELS: Record<MergeCompanyField, string> = {
  legalName: "Razão social",
  tradeName: "Nome fantasia",
  cnpj: "CNPJ",
  site: "Site",
  segment: "Segmento",
  city: "Cidade",
  state: "UF",
};

export function MergeCompaniesDialog({
  source,
  onClose,
  onCompleted,
}: {
  source: Company;
  onClose: () => void;
  onCompleted?: (targetCompanyId: string) => void;
}) {
  const store = useStore();
  const gate = useIdentityGate();
  const previewMerge = store.previewMergeCompanies;
  const sequenceRef = useRef(0);
  const targets = useMemo(
    () => store.companies.filter((company) => company.id !== source.id && company.status === "active"),
    [source.id, store.companies],
  );
  const initialTargetId = targets[0]?.id ?? "";
  const [targetCompanyId, setTargetCompanyId] = useState(initialTargetId);
  const [preview, setPreview] = useState<MergeCompaniesPreview>();
  const [loading, setLoading] = useState(Boolean(initialTargetId));
  const [previewError, setPreviewError] = useState("");
  const [choices, setChoices] = useState<Partial<Record<MergeCompanyField, MergeCompanyFieldChoice>>>({});
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState(
    () => `identity.company.merge:${source.id}:${initialTargetId}:${crypto.randomUUID()}`,
  );

  const target = targets.find((company) => company.id === targetCompanyId);
  const allChoicesMade =
    Boolean(preview) && preview!.conflicts.every((field) => choices[field] === "source" || choices[field] === "target");
  const dirty =
    targetCompanyId !== initialTargetId ||
    Object.values(choices).some((choice) => choice === "source") ||
    Boolean(reason.trim()) ||
    confirmed;

  useEffect(() => {
    const sequence = ++sequenceRef.current;
    let active = true;
    if (!gate.commandsReady || !targetCompanyId) {
      setPreview(undefined);
      setPreviewError("");
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    setPreview(undefined);
    setPreviewError("");
    void previewMerge(source.id, targetCompanyId)
      .then((next) => {
        if (!active || sequence !== sequenceRef.current || !next) return;
        if (next.sourceCompanyId !== source.id || next.targetCompanyId !== targetCompanyId)
          throw new Error("A prévia da mesclagem não corresponde às empresas selecionadas.");
        setPreview(next);
        setChoices((current) => {
          const nextChoices: Partial<Record<MergeCompanyField, MergeCompanyFieldChoice>> = {};
          for (const field of next.conflicts) nextChoices[field] = current[field] ?? "target";
          return nextChoices;
        });
      })
      .catch((cause) => {
        if (active && sequence === sequenceRef.current)
          setPreviewError(
            cause instanceof Error ? cause.message : "Não foi possível carregar a prévia da mesclagem.",
          );
      })
      .finally(() => {
        if (active && sequence === sequenceRef.current) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [gate.commandsReady, previewMerge, source.id, targetCompanyId]);

  const selectTarget = (nextId: string) => {
    setTargetCompanyId(nextId);
    setChoices({});
    setConfirmed(false);
    setError("");
    setIdempotencyKey(`identity.company.merge:${source.id}:${nextId}:${crypto.randomUUID()}`);
  };

  const submit = async () => {
    if (saving) return;
    if (!gate.commandsReady) {
      setError(gate.reason ?? "A mesclagem não está disponível neste ambiente.");
      return;
    }
    if (!target) {
      setError("Escolha uma empresa ativa de destino.");
      return;
    }
    if (!preview || loading) {
      setError("Aguarde a prévia autoritativa antes de confirmar.");
      return;
    }
    if (!allChoicesMade) {
      setError("Escolha a origem de cada campo conflitante.");
      return;
    }
    if (!reason.trim()) {
      setError("Informe o motivo da mesclagem.");
      return;
    }
    if (!confirmed) {
      setError("Confirme a prévia antes de mesclar as empresas.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await store.mergeCompanies({
        sourceCompanyId: source.id,
        targetCompanyId,
        fieldChoices: choices,
        reason: reason.trim(),
        idempotencyKey,
        confirm: true,
      });
      if (!result) return;
      showFeedback(
        result.idempotent ? "Esta mesclagem já estava registrada." : "Empresas mescladas e auditadas.",
        "success",
      );
      if (onCompleted) onCompleted(targetCompanyId);
      else onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Não foi possível mesclar as empresas.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      dirty={dirty}
      width="wide"
      title="Consolidar identidade empresarial"
      description="A origem vira alias auditável. Contatos, canais, leads, jornadas e analytics são preservados; a leitura do destino passa a agregar o histórico."
      footer={
        <>
          <Button variant="text" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <ToolbarSpacer />
          <Button
            variant="primary"
            onClick={submit}
            loading={saving}
            disabled={!gate.commandsReady || !target || loading || !preview}
          >
            Mesclar empresas
          </Button>
        </>
      }
    >
      <div className="cx-channel-head">
        <Building2 size={15} aria-hidden="true" />
        <span>
          <b>{companyName(source)}</b>
          <small>Origem: será preservada como alias somente leitura.</small>
        </span>
      </div>

      <Field label="Empresa de destino" required>
        {({ id, describedBy }) => (
          <Select
            id={id}
            describedBy={describedBy}
            size="field"
            label="Empresa de destino"
            value={targetCompanyId || null}
            placeholder="Nenhuma empresa ativa disponível"
            options={targets.map((company) => ({ value: company.id, label: companyName(company) }))}
            onChange={selectTarget}
            disabled={saving || !gate.commandsReady || targets.length === 0}
            disabledReason={
              targets.length === 0
                ? "Cadastre outra empresa ativa antes de iniciar uma mesclagem."
                : gate.reason
            }
          />
        )}
      </Field>

      {loading && <p className="cx-note" role="status">Atualizando prévia autoritativa…</p>}
      {previewError && (
        <p className="cx-form-alert" role="alert">
          {previewError}
        </p>
      )}

      {preview && target && (
        <div className="cx-preview">
          <div className="cx-preview-head">
            <span>Prévia pronta para confirmação</span>
            <small>{preview.conflicts.length ? `${preview.conflicts.length} conflito(s)` : "Sem conflito"}</small>
          </div>
          <div className="cx-compare">
            <div className="cx-compare-side">
              <small>Origem</small>
              <strong>{companyName(source)}</strong>
              <p>
                {countLabel(preview.sourceCounts.contacts, "contato")} ·{" "}
                {countLabel(preview.sourceCounts.channels, "canal")} ·{" "}
                {countLabel(preview.sourceCounts.leads, "lead")}
              </p>
            </div>
            <span className="cx-compare-arrow" aria-hidden="true">
              <ArrowRight size={16} />
            </span>
            <div className="cx-compare-side">
              <small>Destino, após a leitura agregada</small>
              <strong>{companyName(target)}</strong>
              <p>
                {countLabel(preview.targetCounts.contacts + preview.sourceCounts.contacts, "contato")} ·{" "}
                {countLabel(preview.targetCounts.channels + preview.sourceCounts.channels, "canal")} ·{" "}
                {countLabel(preview.targetCounts.leads + preview.sourceCounts.leads, "lead")}
              </p>
            </div>
          </div>
          {preview.conflicts.length === 0 && (
            <p className="cx-note">
              Nenhum campo diverge. Campos vazios do destino podem ser preenchidos pela origem conforme o
              comando.
            </p>
          )}
        </div>
      )}

      {preview?.conflicts.map((field) => (
        <Field key={field} label={`Valor final de ${MERGE_FIELD_LABELS[field]}`} required>
          {({ id, describedBy }) => (
            <Select
              id={id}
              describedBy={describedBy}
              size="field"
              label={`Valor final de ${MERGE_FIELD_LABELS[field]}`}
              value={choices[field] ?? null}
              options={[
                {
                  value: "target",
                  label: `Manter do destino: ${target?.[field] || "não informado"}`,
                },
                { value: "source", label: `Usar da origem: ${source[field] || "não informado"}` },
              ]}
              onChange={(value) => {
                setChoices((current) => ({ ...current, [field]: value as MergeCompanyFieldChoice }));
                setConfirmed(false);
                setError("");
              }}
              disabled={saving}
            />
          )}
        </Field>
      ))}

      <Field label="Motivo da decisão" required>
        {({ id, describedBy, invalid }) => (
          <TextArea
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            rows={3}
            value={reason}
            disabled={saving || !gate.commandsReady}
            placeholder="Ex.: duplicidade confirmada após revisar CNPJ e evidências"
            onChange={(event) => setReason(event.target.value)}
          />
        )}
      </Field>

      <ConfirmCheck
        checked={confirmed}
        onChange={setConfirmed}
        disabled={saving || !gate.commandsReady || !preview}
      >
        Confirmo a prévia e entendo que a origem se tornará um alias, sem reescrever suas jornadas.
      </ConfirmCheck>

      {!gate.commandsReady && <Notice tone={gate.degraded ? "alert" : "info"}>{gate.reason}</Notice>}
      {error && (
        <p className="cx-form-alert" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
